import { useState } from 'react';
import { apiFetch, NetworkError } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { enqueueOp, removeQueuedOp } from '../../lib/offline/opQueue';
import { getBookingsReadStamp } from '../../lib/offline/cache';
import { stalenessFor } from '../../lib/offline/staleness';
import { C, F, M, fmtTimeInTz, dateKeyInTz, isPending, APPROVAL_STATUS_CFG } from './theme';
import { renderMaintConfirmation } from '../../lib/ops-board/maintConfirmation';
import { useIsMobile } from '../../lib/useIsMobile';
import { inputStyle } from './atoms';
import { externalAircraftLabel } from '../../lib/opsBoardTypes';
import { bookingLabel } from '../../lib/opsBoard/bookingLabel';
import { noEmailParticipants } from '../../lib/notifyWarning';
import type { SeriesAction } from './SeriesActionModal';
import type {
  Aircraft, Asset, Base, Booking, BookingKind, CompanySettings, ParticipantCapacity, ParticipantUser, ReasonCode, Staff, Student, TrainingActivity,
} from '../../lib/opsBoardTypes';

interface Lookups {
  bases: Map<string, Base>;
  aircraft: Map<string, Aircraft>;
  assets: Map<string, Asset>;
  staff: Map<string, Staff>;
  students: Map<string, Student>;
  activities: Map<string, TrainingActivity>;
  bookingKinds: Map<string, BookingKind>;
}

interface Props {
  booking: Booking;
  lookups: Lookups;
  reasonCodes: ReasonCode[]; // Feature C — cancellation-reason picker (online-only)
  canWorkflow: boolean; // ops_controller / admin only
  canApprove: boolean;  // ALL staff — approve / decline / REVOKE (Ben 2026-08-25; backend APPROVAL_ROLES)
  canBook: boolean;     // instructor / ops_controller / admin (booking-rights)
  canCancel: boolean;   // ALL staff — single-booking / single-occurrence cancel (Ben 2026-07-10)
  canMarkAdvised: boolean; // ALL staff — record "client advised of changes" (Ben 2026-09-16; backend CLIENT_ADVISED_ROLES)
  canEditMaintenance: boolean; // senior tier only (backend MAINTENANCE_EDIT_ROLES — PR-000038, Ben 2026-07-22)
  settings: CompanySettings | null;
  openConflictId?: string | null;          // 5d-p3a: open ConflictItem on this booking, if any
  onResolveConflict?: (id: string) => void; // open the resolver
  onClose: () => void;
  onEdit: (b: Booking) => void;
  onCopy: (b: Booking) => void; // recurring/copy Phase 1 — duplicate this booking to a new date
  onSeriesAction: (b: Booking, action: SeriesAction) => void; // recurring Phase 3 — bulk scope (edit/cancel/end)
  // Refetch bookings after a workflow action. `reachedServer: false` = the change only reached the
  // on-device queue (offline) or the queue itself (removing a queued op), so the board must never be
  // told "Saved." (0.12.10 addendum 2). Omitted = the server accepted the write.
  onChanged: (opts?: { reachedServer: boolean }) => void;
}

// Prefilled phone-call confirmation messages (kickoff §4b). Composed SERVER-SIDE
// (GET /api/bookings/:id/approval-templates) so the booking's own name / activity / registration /
// timezone helpers are reused rather than reimplemented here.
interface ConfirmationTemplate { contact_name: string | null; registration: string | null; message: string }
interface ApprovalTemplates { staff: ConfirmationTemplate | null; asset: ConfirmationTemplate | null }

// Booking history (PR-000054). Shaped entirely SERVER-SIDE: the endpoint returns humanised
// lines, never the raw old_data / new_data snapshots, so nothing here has to know a Task column
// name or decide what a change means. `recorded: false` marks a line reconstructed from the
// booking row because no audit entry exists for it, and it is labelled as such in the panel.
interface HistoryChange { field: string; label: string; from: string; to: string }
interface HistoryEntry {
  id: string; task_id: string; at: string; at_label: string; actor: string;
  action: string; action_label: string; summary: string; reason: string | null;
  changes: HistoryChange[]; recorded: boolean;
}
interface HistoryResult { entries: HistoryEntry[]; truncated: boolean; notes: string[]; series_id: string | null }

// Colour per entry class, so a withdrawal does not read like an ordinary edit at a glance.
// approval_withdrawn is deliberately its OWN colour rather than sharing `declined`'s: an
// approval pulled BACK after being granted is a different operational event from one that was
// never given, and the panel has to show that difference, not just record it.
const HISTORY_TONE: Record<string, string> = {
  created: C.accent,
  approved: C.green,
  maintenance_returned: C.green,
  allocated: C.green,
  declined: C.red,
  cancelled: C.red,
  approval_withdrawn: C.purple,
  orphaned: C.amber,
  waitlisted: C.amber,
  reminder_sent: C.textMuted,
  // Amber, not muted: this one is an alarm, and it should not read like the routine reminder.
  escalation_sent: C.amber,
  backfill: C.textMuted,
  client_advised: C.green, // 0.12.7 Part A
};

const CAPACITY_LABELS: Record<ParticipantCapacity, string> = {
  instructor: 'Instructor', student: 'Student', client: 'Client',
  external_instructor: 'External Instructor', external_examiner: 'External Examiner',
};
const CAPACITY_ORDER: ParticipantCapacity[] = ['instructor', 'external_instructor', 'external_examiner', 'student', 'client'];
// Capacities on the non-instructor person line — notified like students (§11.9).
const STUDENT_LINE_CAPACITIES: ParticipantCapacity[] = ['student', 'client'];

type Mode = 'view' | 'approve' | 'decline' | 'cancel' | 'return';

// What happens to the booking when it is declined or its approval is withdrawn (kickoff §3.1).
// MANDATORY, no default: '' means "not chosen yet" and the confirm button stays inert. Silently
// picking one is how bookings get lost.
type Disposition = '' | 'waitlist' | 'cancel';

// "now" formatted for a <input type=datetime-local> (local wall-clock, minute precision).
function nowLocalInput(): string {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

export default function BookingDetail({ booking, lookups, reasonCodes, canWorkflow, canApprove, canBook, canCancel, canMarkAdvised, canEditMaintenance, settings, openConflictId, onResolveConflict, onClose, onEdit, onCopy, onSeriesAction, onChanged }: Props) {
  const { user } = useAuth(); // 5d-p2: offline cancel ops are queued per-user
  const isMobile = useIsMobile();
  const [mode, setMode] = useState<Mode>('view');
  const [seriesScope, setSeriesScope] = useState<null | 'edit' | 'cancel'>(null); // Phase 3 — 3-way chooser
  const [reason, setReason] = useState('');               // decline / revocation reason (mandatory)
  const [disposition, setDisposition] = useState<Disposition>(''); // kickoff §3.1 — forced choice
  const [approveNote, setApproveNote] = useState('');     // kickoff §4b — mandatory written evidence
  const [templates, setTemplates] = useState<ApprovalTemplates | null>(null);
  const [copied, setCopied] = useState<'staff' | 'asset' | null>(null);
  const [cancelReasonId, setCancelReasonId] = useState(''); // Feature C — coded cancellation reason
  const [cancelNote, setCancelNote] = useState('');         // optional free-text note alongside the code
  const cancelReasons = reasonCodes.filter((r) => r.context === 'cancellation');
  const [returnAt, setReturnAt] = useState('');
  // Late-return reason capture (maintenance, Ben 2026-06-19) — prompted only when the entered
  // return time is after the planned ECD (arr_time_planned).
  const [lateReasonGiven, setLateReasonGiven] = useState<'unset' | 'yes' | 'no'>('unset');
  const [lateReasonText, setLateReasonText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [advisory, setAdvisory] = useState<string | null>(null); // 5d-p5 inline staleness advisory (non-blocking)
  // History (PR-000054). LAZY: nothing is fetched until the section is expanded, so opening a
  // booking costs exactly what it costs today. `historyOffline` is separate from `historyError`
  // because the two need different copy: unreachable is a state to wait out, a real error is not.
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState<HistoryResult | null>(null);
  const [historyBusy, setHistoryBusy] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [historyOffline, setHistoryOffline] = useState(false);
  const [openEntryId, setOpenEntryId] = useState<string | null>(null); // drill-down, one at a time

  // 5d-p2 overlay states. A LOCAL-PENDING row is synthesized from a queued op (it does
  // not exist server-side yet) — workflow actions are meaningless against it; the only
  // affordances are Edit (amends the queued op) and removing the queued op.
  const localPending = !!booking._pending_op;
  const localPendingCreate = booking._pending_op?.kind === 'create';
  const conflicted = booking.sync_state === 'conflicted';

  const base = lookups.bases.get(booking.base_id);
  const tz = booking.dep_timezone;
  const appCfg = APPROVAL_STATUS_CFG[booking.approval_status] ?? { color: C.textMuted, bg: C.pill, label: booking.approval_status, icon: '' };
  const pending = isPending(booking.approval_status);
  // Revocation source state (kickoff §4). An approved booking is now a valid decline target; a
  // cancelled one is not (there is nothing left to release).
  const approved = booking.approval_status === 'approved' && booking.booking_status !== 'cancelled';

  // Participant display name (falls back to the lookups if the embedded user is absent).
  // Coded staff render `CODE - First Last` (mirrors staffLabel in BookingForm — structured
  // name falls back to full_name). Codeless people (students/clients) keep the current display.
  const personLabel = (p: { full_name: string; preferred_name: string | null; pilot_code?: string | null; first_name?: string | null; last_name?: string | null }): string => {
    const code = p.pilot_code?.trim();
    if (!code) return p.preferred_name ?? p.full_name;
    const namePart = [p.first_name, p.last_name].filter(Boolean).join(' ').trim() || p.full_name;
    return `${code} - ${namePart}`;
  };
  const partName = (uid: string, embedded: ParticipantUser | null): string => {
    if (embedded) return personLabel(embedded);
    const s = lookups.staff.get(uid) ?? lookups.students.get(uid);
    return s ? personLabel(s) : uid;
  };
  // External participants have no user_id; show their typed name. Internals resolve via lookups.
  const participantName = (p: Booking['participants'][number]): string =>
    p.user_id == null ? (p.external_name ?? '—') : partName(p.user_id, p.user);
  // Group by capacity (instructors → externals → students), ranked within group.
  const participants = [...booking.participants].sort((a, b) => {
    const co = CAPACITY_ORDER.indexOf(a.capacity) - CAPACITY_ORDER.indexOf(b.capacity);
    if (co !== 0) return co;
    if (a.rank != null && b.rank != null) return a.rank - b.rank;
    if (a.rank != null) return -1;
    if (b.rank != null) return 1;
    return 0;
  });
  // Students/clients with no usable email address. The ADDRESS never reaches the client — the
  // server derives the boolean and strips the email (bookings.service.ts serializeBooking).
  const noEmail = noEmailParticipants(booking);

  // Waitlist allocation authority (per-tenant policy).
  const policy = settings?.waitlist_allocation_policy ?? 'ops_admin';
  const canAllocate = booking.waitlisted && booking.booking_status !== 'cancelled' && (policy === 'any_instructor' ? canBook : canWorkflow);

  const acAllocs = booking.asset_allocations.filter((a) => a.aircraft_registration);
  const assetAllocs = booking.asset_allocations.filter((a) => a.asset_id);
  const extAllocs = booking.asset_allocations.filter((a) => a.external_aircraft_source);

  const kind = booking.booking_kind_id ? lookups.bookingKinds.get(booking.booking_kind_id) : null;
  const isMaintenance = !!kind?.is_maintenance;
  // 0.12.7 Part D: the stored note is what was TYPED; the sentence is rendered at read time, in
  // maintConfirmation.ts, which also shows pre-0.12.7 sentences exactly as stored.
  const maintAlloc = acAllocs.find((a) => a.booking_confirmation_note);
  const maintNote = maintAlloc ? renderMaintConfirmation(maintAlloc, tz) : null;
  const returned = !!booking.maintenance_returned_at;

  const timeRange = booking.block_start_at
    ? `${fmtTimeInTz(booking.block_start_at, tz)}${booking.arr_time_planned ? `–${fmtTimeInTz(booking.arr_time_planned, tz)}` : ''}`
    : 'Time TBC';

  async function act(path: string, body?: Record<string, unknown>) {
    setError(null);
    setBusy(true);
    try {
      const res = await apiFetch<Booking>(`/api/bookings/${booking.id}/${path}`, {
        method: 'POST',
        body: body ? JSON.stringify(body) : undefined,
      });
      if (res.error) {
        setError(res.error.message);
        return;
      }
      onChanged();
    } catch (e) {
      // 5d-p2/p2b: CANCEL (waitlist_leave when the booking is waitlisted) and
      // MAINTENANCE-RETURN are offline op types — a network rejection queues them.
      // Approve / decline / allocate stay online-only (v2 §3.3 / Q7 DECIDED) and keep
      // the plain failure message.
      if (e instanceof NetworkError && user && !localPending && (path === 'cancel' || path === 'maintenance-return')) {
        const opType = path === 'maintenance-return'
          ? 'maintenance_return' as const
          : booking.waitlisted ? 'waitlist_leave' as const : 'booking_cancel' as const;
        // 5d-p5: per-action staleness advisory — waitlist_leave is Class A, booking_cancel is
        // Class B, maintenance_return is class 'none' (never advises). Recorded on the op +
        // surfaced inline (non-blocking — the op queues regardless).
        const readStamp = getBookingsReadStamp();
        const stale = stalenessFor(opType, readStamp);
        const op = await enqueueOp(user.id, opType, { task_id: booking.id, ...body }, readStamp, stale.record);
        if (op) { if (stale.message) setAdvisory(stale.message); onChanged({ reachedServer: false }); return; }
        setError('Offline, and the on-device queue is unavailable — this change could not be saved.');
        return;
      }
      setError(e instanceof Error ? e.message : 'Request failed.');
    } finally {
      setBusy(false);
    }
  }

  // Phone-call confirmation templates (kickoff §4b). Fetched when the approve dialog opens, not on
  // every booking open: most opens are not approvals. Online-only, like approve itself; a failure
  // leaves the dialog fully usable (the mandatory note is what the server actually requires).
  async function loadTemplates() {
    setTemplates(null);
    setCopied(null);
    try {
      const res = await apiFetch<ApprovalTemplates>(`/api/bookings/${booking.id}/approval-templates`);
      if (!res.error && res.data) setTemplates(res.data);
    } catch {
      /* offline or unreachable — the dialog degrades to the note alone. */
    }
  }

  async function copyTemplate(which: 'staff' | 'asset', text: string) {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(which);
    } catch {
      // Clipboard access is blocked on insecure origins and in some mobile webviews. Say so
      // rather than silently doing nothing — the text is on screen and can be selected by hand.
      setError('Could not copy automatically. Select the message text and copy it.');
    }
  }

  // Remove a still-queued CREATE — nothing exists server-side, the op just leaves the queue.
  async function removeQueued() {
    if (!booking._pending_op) return;
    await removeQueuedOp(booking._pending_op.queueKey);
    onChanged({ reachedServer: false });
    onClose();
  }

  const btn = (label: string, color: string, bg: string, border: string, onClick: () => void, flex = 1) => (
    <button
      onClick={onClick}
      disabled={busy}
      style={{ flex, background: bg, border: `1px solid ${border}`, color, borderRadius: 5, padding: '6px', fontSize: 11, fontWeight: 700, cursor: busy ? 'default' : 'pointer', fontFamily: F, opacity: busy ? 0.6 : 1 }}
    >
      {label}
    </button>
  );

  // History is ONLINE-ONLY reference data and deliberately NOT added to the offline cache (v1).
  // A booking's history is the one thing on this panel that is never needed to fly: it is read
  // when someone asks what happened, which is a desk activity. Caching it would mean shipping
  // audit rows to every device for every booking they hold.
  const loadHistory = async () => {
    setHistoryBusy(true); setHistoryError(null); setHistoryOffline(false);
    try {
      const r = await apiFetch<HistoryResult>(`/api/bookings/${booking.id}/history`);
      if (r.error) { setHistoryError(typeof r.error === 'string' ? r.error : r.error.message); }
      else setHistory(r.data);
    } catch (e) {
      if (e instanceof NetworkError) setHistoryOffline(true);
      else setHistoryError('History could not be loaded.');
    } finally {
      setHistoryBusy(false);
    }
  };

  return (
    <>
    {/* Mobile: a dismissable scrim behind the bottom sheet. Desktop: floating card, no scrim. */}
    {isMobile && <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.45)', zIndex: 199 }} />}
    <div
      style={{
        position: 'fixed', zIndex: 200, fontFamily: F,
        background: C.surface, border: `1px solid ${pending ? C.amberBorder : C.borderMed}`,
        boxShadow: '0 8px 32px rgba(0,0,0,0.16)',
        // THE PANEL SCROLLS ITS BODY, and the header stays put (2026-08-27, found in a live check
        // of the approve dialog). Desktop previously had overflow:'hidden' and NO maxHeight, so a
        // panel taller than the viewport was CLIPPED with no way to reach the rest — the aircraft
        // template and everything below it were simply unreachable. Mobile did scroll, but the
        // whole card scrolled, taking the booking title and the close button off screen with it.
        //
        // Flex column + a bounded height + an overflow:auto BODY fixes both, and keeps the dark
        // header (which carries ✕) pinned. overflow:'hidden' stays on the container so the rounded
        // corners still clip; the scrolling moved one level in, to the body.
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        ...(isMobile
          ? { left: 8, right: 8, bottom: 0, width: 'auto', maxHeight: '85vh', borderRadius: '12px 12px 0 0' }
          // top:96 with a 24px breathing space at the bottom — the same inset as `right`.
          : { right: 24, top: 96, width: 312, maxHeight: 'calc(100vh - 120px)', borderRadius: 10 }),
      }}
    >
      <div style={{ background: pending ? '#92400e' : C.navy, padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>{bookingLabel(booking, lookups.bookingKinds, lookups.activities, { allowSuffix: true })}</div>
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.6)', marginTop: 2 }}>
            {timeRange} {base ? `· ${base.short_code}` : ''}
          </div>
        </div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)', cursor: 'pointer', fontSize: 18 }}>
          ✕
        </button>
      </div>

      <div style={{ padding: '12px 16px', flex: 1, minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
        {/* Approval + booking status */}
        <div style={{ background: appCfg.bg, border: `1px solid ${appCfg.color}55`, borderRadius: 6, padding: '8px 10px', marginBottom: 10 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: appCfg.color, marginBottom: 2 }}>
            {appCfg.icon} {appCfg.label}
            <span style={{ float: 'right', color: C.textMuted, fontWeight: 600 }}>{booking.booking_status}</span>
          </div>
          {/* ⚠ THE "✓ Student notified" / "⚠ Student NOT notified yet" PAIR IS GONE (Ben, 2026-09-17).
              Until booking notification emails exist, ASSUME EVERY STUDENT AND CLIENT HAS BEEN TOLD.
              student_notified records an APPROVAL OUTCOME (§11.9: not notified until approved), not
              that a human rang anybody, so both lines stated something the system could not know —
              and the green half was the worse of the two, because it reassured. The field is still
              WRITTEN exactly as before; it is simply no longer DISPLAYED. What is left is a warning
              and only a warning. */}
          {booking.outside_hours && <div style={{ fontSize: 10, color: C.amber, marginTop: 2 }}>⏰ Outside operating hours</div>}
          {/* THREE states from two fields (Ben, 2026-09-08; wording updated 2026-09-17):
                modified_after_notify false            → nothing
                modified, modify_notified_at null      → amber phone (nobody has rung them yet)
                modified, modify_notified_at set       → green tick REPLACING it, with who and when
              The server decides what counts as a modification worth a call — a start or end time
              change, or a change of aircraft TYPE (updateBooking's notifiableChange). An instructor
              swap, a like-for-like aircraft swap, a title edit and a no-op save do NOT reach here.
              Every notifiable change nulls modify_notified_at server-side, so a fresh change always
              brings the warning back over the tick. The green line is the RECORD OF WHO MADE THE
              CALL and stays exactly as it was. */}
          {booking.modified_after_notify && (booking.modify_notified_at ? (
            <div style={{ fontSize: 10, color: C.green, marginTop: 2 }}>
              ✓ Client advised of changes{booking.modify_notified_by ? ` by ${partName(booking.modify_notified_by, null)}` : ''} · {dateKeyInTz(booking.modify_notified_at, tz)} {fmtTimeInTz(booking.modify_notified_at, tz)}
            </div>
          ) : (
            <div style={{ fontSize: 10, color: C.amber, marginTop: 2 }}>
              ☎ Changed since the client was told
              {/* ALL staff, not the write tier (Ben 2026-09-16; backend CLIENT_ADVISED_ROLES). The
                  displacement notice names the SBP as the backup caller, and an SBP holds no
                  booking-write role — canBook here would hide the control from the very person
                  the notice asked to ring. */}
              {canMarkAdvised && !localPending && (
                <button
                  onClick={() => act('client-advised')}
                  disabled={busy}
                  style={{ marginLeft: 6, background: 'none', border: `1px solid ${C.amberBorder}`, color: C.amber, borderRadius: 4, padding: '0 5px', fontSize: 10, fontWeight: 700, cursor: busy ? 'default' : 'pointer', fontFamily: F }}
                >
                  Mark client advised
                </button>
              )}
            </div>
          ))}
          {/* The OTHER reason somebody has to ring: notification will rely on email, and this
              student or client has no usable address. Same amber phone symbol as the line above,
              because to the operator it is the same job. One line per affected person.
              noEmailParticipants is the SAME predicate the board chip reads (lib/notifyWarning.ts),
              so the panel and the chip cannot come to different conclusions. */}
          {noEmail.map((p) => (
            <div key={`nomail-${p.id}`} style={{ fontSize: 10, color: C.amber, marginTop: 2 }}>
              ☎ {CAPACITY_LABELS[p.capacity] ?? p.capacity} not yet notified: no email address
            </div>
          ))}
        </div>

        {/* Recurring series (Phase 3) — generated from a BookingSeries. Single edit/cancel acts on
            THIS occurrence; the series-management actions act across the series (step-up). */}
        {booking.series_id && (
          <div style={{ background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 6, padding: '8px 10px', marginBottom: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#4f46e5' }}>🔁 Part of a recurring series</div>
            <div style={{ fontSize: 10, color: C.textMuted, marginTop: 2, marginBottom: 6 }}>
              {booking.participants.length === 0 && booking.asset_allocations.length === 0
                ? 'Unassigned — add crew and aircraft, then Save to allocate this day.'
                : 'Edits apply to this occurrence only unless you choose a series scope.'}
            </div>
            {/* Phase 4 — per-occurrence clamp + assignee-conflict flags. */}
            {booking.series_clamped && (
              <div style={{ fontSize: 10, color: C.amber, marginBottom: 6 }}>
                📅 Moved to the last day of the month (the intended date doesn't exist here).
              </div>
            )}
            {booking.series_assignee_conflict && (
              <div style={{ background: C.redLight, border: `1px solid ${C.redBorder}`, borderRadius: 5, padding: '6px 8px', marginBottom: 6, fontSize: 10.5, color: C.red, fontWeight: 600 }}>
                ⚠ Assignee double-booked — the default pilot clashes with another booking at this time. Reassign or clear the pilot below to resolve.
              </div>
            )}
            {booking.booking_status !== 'cancelled' && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {canBook && btn('Edit series', '#4f46e5', '#fff', '#c7d2fe', () => onSeriesAction(booking, { kind: 'edit', scope: 'all' }))}
                {canWorkflow && btn('Stop recurring', C.red, C.redLight, C.redBorder, () => onSeriesAction(booking, { kind: 'end' }))}
              </div>
            )}
          </div>
        )}

        {/* 5d-p2 provisional / conflict banners (v2 §§4–5) */}
        {localPending && (
          <div style={{ background: '#f5f3ff', border: '1px solid #c4b5fd', borderRadius: 6, padding: '8px 10px', marginBottom: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#7c3aed' }}>⌛ Provisional — saved on this device, checks pending</div>
            <div style={{ fontSize: 10, color: C.textMuted, marginTop: 2 }}>
              {booking._pending_op?.kind === 'cancel'
                ? 'A cancellation is queued and will sync when reconnected.'
                : 'It will sync and be availability-checked when this device reconnects.'}
            </div>
          </div>
        )}
        {/* 5d-p3a: open ConflictItem on this booking → flag + deep-link to the resolver.
            Covers BOTH slot_clash (sync_state='conflicted') and edit_divergence (sync_state
            null — no Phase 2 banner, but still has an open conflict to resolve). */}
        {openConflictId && !localPending ? (
          <div style={{ background: C.redLight, border: `1px solid ${C.redBorder}`, borderRadius: 6, padding: '8px 10px', marginBottom: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.red }}>⚠ Conflict — needs attention</div>
            <div style={{ fontSize: 10, color: C.textMuted, marginTop: 2, marginBottom: 6 }}>
              {conflicted
                ? 'This offline booking clashed with another booking at sync. It does not hold the slot until resolved.'
                : 'This booking was edited offline and elsewhere before sync. Review both versions.'}
            </div>
            {onResolveConflict && (
              <button onClick={() => onResolveConflict(openConflictId)}
                style={{ background: C.red, border: 'none', color: '#fff', borderRadius: 5, padding: '5px 10px', fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: F }}>
                Review &amp; resolve →
              </button>
            )}
          </div>
        ) : conflicted && !localPending ? (
          <div style={{ background: C.redLight, border: `1px solid ${C.redBorder}`, borderRadius: 6, padding: '8px 10px', marginBottom: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.red }}>⚠ Conflict — needs attention</div>
            <div style={{ fontSize: 10, color: C.textMuted, marginTop: 2 }}>
              This offline booking clashed with another booking at sync. An ops controller or admin resolves it; it does not hold the slot meanwhile.
            </div>
          </div>
        ) : null}

        {/* Waitlist banner (Step 5c.3 §B) */}
        {booking.waitlisted && (
          <div style={{ background: C.amberLight, border: `1px solid ${C.amberBorder}`, borderRadius: 6, padding: '8px 10px', marginBottom: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.amber }}>⏳ On the waitlist — holding its requested time</div>
            {booking.waitlisted_at && <div style={{ fontSize: 10, color: C.textMuted, marginTop: 2 }}>Waitlisted {booking.waitlisted_at.slice(0, 10)}</div>}
          </div>
        )}

        {/* Key details */}
        {[
          ['Aircraft', acAllocs.length ? acAllocs.map((a) => a.aircraft_registration).join(', ') : '—'],
          ...(extAllocs.length ? [['External aircraft', extAllocs.map((a) => externalAircraftLabel(a) + (a.external_aircraft_invoicer ? ` — invoiced by ${a.external_aircraft_invoicer}` : '')).join(', ')]] as [string, string][] : []),
          ['Asset', assetAllocs.length ? assetAllocs.map((a) => lookups.assets.get(a.asset_id!)?.name ?? a.asset_id).join(', ') : '—'],
          ['Base', base?.name ?? booking.base_id],
          ...(booking.max_participants != null ? [['Limit', `${booking.max_participants}${booking.participants_limit_hard ? ' (fixed group)' : ' (desired max)'}`]] as [string, string][] : []),
        ].map(([l, v]) => (
          <div key={l} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, gap: 8 }}>
            <span style={{ fontSize: 11, color: C.textMuted, minWidth: 72 }}>{l}</span>
            <span style={{ fontSize: 11, color: C.textDim, textAlign: 'right', fontFamily: M }}>{v}</span>
          </div>
        ))}

        {/* Participants (Step 5c.3) */}
        <div style={{ marginTop: 4, marginBottom: 4 }}>
          <div style={{ fontSize: 9, fontWeight: 700, color: C.textMuted, letterSpacing: '0.06em', marginBottom: 4 }}>PARTICIPANTS ({participants.length})</div>
          {participants.length === 0 && <div style={{ fontSize: 11, color: C.textMuted }}>None.</div>}
          {participants.map((p) => (
            <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 3 }}>
              <span style={{ fontSize: 11, color: C.textDim }}>
                {p.rank != null && <span style={{ color: C.textMuted, fontFamily: M }}>{p.rank}. </span>}
                {participantName(p)}
                <span style={{ color: C.textMuted }}> — {CAPACITY_LABELS[p.capacity] ?? p.capacity}</span>
              </span>
              {/* ⚠ "✓ notified" / "⚠ not notified" REMOVED HERE TOO (Ben, 2026-09-17) — same reason
                  as the block at the top of the panel: `notified` is an approval outcome, not a
                  record of anybody being told. The only per-person fact worth showing is the one
                  that will actually stop a notification going out. */}
              {STUDENT_LINE_CAPACITIES.includes(p.capacity) && p.email_missing === true && (
                <span style={{ fontSize: 9, color: C.amber, whiteSpace: 'nowrap' }}>⚠ no email address</span>
              )}
            </div>
          ))}
        </div>

        {booking.activity_note && (
          <div style={{ background: C.surfaceAlt, borderRadius: 5, padding: '7px 10px', marginTop: 8, fontSize: 11, color: C.textDim, lineHeight: 1.5 }}>
            {booking.activity_note}
          </div>
        )}

        {/* Staff-only note — its OWN clearly labelled block, shown only when non-empty, and
            visually distinct from the shared note above so the two are never confused. This
            detail panel is staff-only (both booking read routes are staff-gated). */}
        {booking.internal_note && (
          <div style={{ background: C.surfaceAlt, border: `1px solid ${C.borderMed}`, borderRadius: 5, padding: '7px 10px', marginTop: 8, fontSize: 11, color: C.textDim, lineHeight: 1.5 }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: C.slate, letterSpacing: '0.06em', marginBottom: 3 }}>🔒 INTERNAL NOTE (STAFF ONLY)</div>
            {booking.internal_note}
          </div>
        )}

        {/* The reminder is CONTENT GATED (amendment §4.1): it goes out when the booking has a
            staff note or an outstanding approval, or when "Always remind the instructor" is
            ticked. So this line must mirror the same three reasons the sweep uses, not just the
            checkbox — otherwise a booking that WILL be reminded about would show nothing here. */}
        {(() => {
          const willRemind = booking.reminder_enabled
            || !!booking.internal_note?.trim()
            || ['pending_staff', 'pending_asset', 'pending_both'].includes(booking.approval_status);
          if (!willRemind) return null;
          return (
            <div style={{ marginTop: 6, fontSize: 10, color: C.textMuted }}>
              {booking.reminder_sent_at
                ? '⏰ Booking reminder sent to the instructor.'
                : booking.reminder_enabled
                  ? '⏰ The instructor will always be reminded, the day before this booking.'
                  : '⏰ The instructor will be reminded the day before this booking.'}
            </div>
          );
        })()}

        {/* Maintenance lifecycle (Step 5c.2.1). PR-000027: gate on a FLEET aircraft allocation
            (acAllocs) to match the "Confirm returned" button — a maintenance-kind booking with
            only external/cross-hire aircraft never enters the return lifecycle, so it must not
            show the 🔧/"Awaiting return confirmation" banner. Covers legacy bookings created
            before the create/update hard-block below. */}
        {isMaintenance && acAllocs.length > 0 && (
          <div style={{ background: '#eff6ff', border: `1px solid ${C.accentBorder}`, borderRadius: 5, padding: '7px 10px', marginTop: 8, fontSize: 11, color: C.accent, lineHeight: 1.5 }}>
            🔧 {kind?.name}
            {maintNote && <div style={{ color: C.textDim, marginTop: 2 }}>{maintNote}</div>}
            {acAllocs[0]?.aircraft_registration && booking.arr_time_planned && (
              <div style={{ color: C.textMuted, marginTop: 2 }}>ECD {fmtTimeInTz(booking.arr_time_planned, tz)} · {booking.arr_time_planned.slice(0, 10)}</div>
            )}
            {returned ? (
              <div style={{ color: C.green, marginTop: 2, fontWeight: 700 }}>✓ Confirmed back in service</div>
            ) : (
              <div style={{ color: C.amber, marginTop: 2 }}>Awaiting return confirmation</div>
            )}
          </div>
        )}

        {booking.booking_status === 'cancelled' && (
          <div style={{ background: C.redLight, borderRadius: 5, padding: '7px 10px', marginTop: 8, fontSize: 11, color: C.red, lineHeight: 1.5 }}>
            Cancelled{booking.cancelled_at ? ` ${booking.cancelled_at.slice(0, 10)}` : ''}
            {booking.cancelled_by ? ` by ${partName(booking.cancelled_by, null)}` : ''}
            {booking.cancel_reason ? `: ${booking.cancel_reason}` : '.'}
          </div>
        )}

        {error && (
          <div style={{ background: C.redLight, border: `1px solid ${C.redBorder}`, borderRadius: 5, padding: '7px 10px', marginTop: 8, fontSize: 11, color: C.red }}>
            ⚠ {error}
          </div>
        )}

        {/* 5d-p5 staleness advisory — inline, non-blocking (the action already queued). */}
        {advisory && (
          <div style={{ background: C.amberLight, border: `1px solid ${C.amberBorder}`, borderRadius: 5, padding: '7px 10px', marginTop: 8, fontSize: 11, color: C.amber, lineHeight: 1.5 }}>
            ⚠ {advisory}
          </div>
        )}

        {/* Actions. LOCAL-PENDING rows exist only in this device's queue: workflow
            actions are hidden — Edit amends the queued op; a queued CREATE can be
            removed outright (the op leaves the queue, nothing exists server-side). */}
        {mode === 'view' && localPending && (
          <div style={{ marginTop: 14, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {localPendingCreate && btn('Edit', C.accent, C.accentLight, C.accentBorder, () => onEdit(booking))}
            {localPendingCreate && btn('Remove queued booking', C.red, C.redLight, C.redBorder, () => { void removeQueued(); })}
          </div>
        )}
        {mode === 'view' && !localPending && (
          <div style={{ marginTop: 14, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {canAllocate && btn('⤴ Allocate from waitlist', C.accent, C.accentLight, C.accentBorder, () => act('allocate', {}), 2)}
            {canApprove && pending && !conflicted && btn('✓ Approve', C.green, C.greenLight, C.green, () => { setMode('approve'); setApproveNote(''); setError(null); void loadTemplates(); }, 2)}
            {canWorkflow && isMaintenance && acAllocs.length > 0 && !returned && booking.booking_status !== 'cancelled' &&
              btn('🔧 Confirm returned', C.green, C.greenLight, C.green, () => { setMode('return'); setReturnAt(nowLocalInput()); setLateReasonGiven('unset'); setLateReasonText(''); setError(null); }, 2)}
            {/* Edit — a series occurrence opens the 3-way scope chooser; otherwise the direct edit.
                Maintenance bookings: senior tier only (backend enforces; hiding avoids a doomed form). */}
            {booking.booking_status !== 'cancelled' && (!isMaintenance || canEditMaintenance) && btn('Edit', C.accent, C.accentLight, C.accentBorder, () => (booking.series_id ? setSeriesScope('edit') : onEdit(booking)))}
            {/* Postpone (Phase 3) — a "this occurrence" date change (keeps the series slot consumed). */}
            {booking.series_id && booking.booking_status !== 'cancelled' && (!isMaintenance || canEditMaintenance) && btn('Postpone', C.accent, C.accentLight, C.accentBorder, () => onEdit(booking))}
            {/* Copy… (recurring/copy Phase 1) — open a prefilled NEW booking on a new date. */}
            {canBook && btn('Copy…', C.navy, C.surface, C.border, () => onCopy(booking))}
            {/* Decline, and — since 2026-08-25 — WITHDRAW an approval already given ("I was
                available but now I'm not"). Same dialog for both entry points, as Ben asked. */}
            {canApprove && (pending || approved) && btn(approved ? 'Withdraw approval' : 'Decline', C.red, C.redLight, C.redBorder,
              () => { setMode('decline'); setReason(''); setDisposition(''); setCancelReasonId(''); setError(null); })}
            {canCancel && booking.booking_status !== 'cancelled' && btn('Cancel', C.red, C.redLight, C.redBorder, () => (booking.series_id ? setSeriesScope('cancel') : (setMode('cancel'), setCancelReasonId(''), setCancelNote(''))))}
          </div>
        )}

        {/* 3-way scope chooser (Phase 3) — shown when editing/cancelling a series occurrence. */}
        {mode === 'view' && seriesScope && (
          <div style={{ marginTop: 12, background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 8, padding: '10px 12px' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.text, marginBottom: 2 }}>This is part of a recurring booking.</div>
            <div style={{ fontSize: 10, color: C.textMuted, marginBottom: 8 }}>{seriesScope === 'edit' ? 'Apply your edit to…' : 'Cancel…'}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {seriesScope === 'edit' ? (
                <>
                  {btn('This occurrence', C.accent, C.accentLight, C.accentBorder, () => { setSeriesScope(null); onEdit(booking); })}
                  {btn('This and all following', C.navy, C.surface, C.border, () => { setSeriesScope(null); onSeriesAction(booking, { kind: 'edit', scope: 'following' }); })}
                  {btn('All in the series', C.navy, C.surface, C.border, () => { setSeriesScope(null); onSeriesAction(booking, { kind: 'edit', scope: 'all' }); })}
                </>
              ) : (
                <>
                  {canCancel && btn('This occurrence', C.red, C.redLight, C.redBorder, () => { setSeriesScope(null); setMode('cancel'); setCancelReasonId(''); setCancelNote(''); })}
                  {canWorkflow && btn('This and all following', C.red, C.redLight, C.redBorder, () => { setSeriesScope(null); onSeriesAction(booking, { kind: 'cancel', scope: 'following' }); })}
                  {canWorkflow && btn('All in the series', C.red, C.redLight, C.redBorder, () => { setSeriesScope(null); onSeriesAction(booking, { kind: 'cancel', scope: 'all' }); })}
                  {!canWorkflow && <div style={{ fontSize: 10, color: C.textMuted }}>Cancelling this and following / all in the series requires ops/admin.</div>}
                </>
              )}
              {btn('Back', C.textMuted, 'none', C.border, () => setSeriesScope(null))}
            </div>
          </div>
        )}

        {/* ── History (PR-000054) ────────────────────────────────────────────────────────
            Collapsed by default and lazily fetched on first expand. Hidden for a LOCAL-PENDING
            booking: it exists only in this device's op queue, so there is no server record to
            have a history of, and offering the section would only ever produce a 404. */}
        {mode === 'view' && !seriesScope && !localPending && (
          <div style={{ marginTop: 14, borderTop: `1px solid ${C.border}`, paddingTop: 10 }}>
            <button
              onClick={() => { const next = !historyOpen; setHistoryOpen(next); if (next && !history && !historyBusy) void loadHistory(); }}
              style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: F, fontSize: 11, fontWeight: 700, color: C.text }}
            >
              <span style={{ fontSize: 9, color: C.textMuted }}>{historyOpen ? '▾' : '▸'}</span>
              History{history ? ` (${history.entries.length}${history.truncated ? '+' : ''})` : ''}
              {historyBusy && <span style={{ fontWeight: 400, color: C.textMuted }}>loading…</span>}
            </button>

            {historyOpen && (
              <div style={{ marginTop: 8 }}>
                {/* Offline: SAY SO. An empty panel would read as "nothing ever happened". */}
                {historyOffline && (
                  <div style={{ fontSize: 10.5, color: C.amber, background: C.amberLight, border: `1px solid ${C.amberBorder}`, borderRadius: 5, padding: '6px 9px' }}>
                    History needs a connection. It is not stored on this device.
                  </div>
                )}
                {historyError && (
                  <div style={{ fontSize: 10.5, color: C.red, background: C.redLight, border: `1px solid ${C.redBorder}`, borderRadius: 5, padding: '6px 9px' }}>
                    ⚠ {historyError}
                  </div>
                )}

                {/* Server notes: an older-than-the-audit-log booking, a series occurrence whose
                    series events live elsewhere, a truncated read. These are the whole point of
                    the locked decision that the UI must STATE the gap rather than render blank. */}
                {history?.notes.map((n) => (
                  <div key={n} style={{ fontSize: 10, color: C.textDim, background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 5, padding: '6px 9px', marginBottom: 6, lineHeight: 1.5 }}>
                    {n}
                  </div>
                ))}

                {/* Newest first: the last thing that happened is what people came to read. */}
                {history && [...history.entries].reverse().map((e) => {
                  const tone = HISTORY_TONE[e.action] ?? C.slate;
                  const expandable = e.changes.length > 0;
                  const open = openEntryId === e.id;
                  return (
                    <div key={e.id} style={{ borderLeft: `2px solid ${tone}`, paddingLeft: 8, marginBottom: 8 }}>
                      <div style={{ fontSize: 9.5, color: C.textMuted }}>
                        {e.at_label} · {e.actor}
                      </div>
                      <div style={{ fontSize: 10.5, color: tone, fontWeight: 700 }}>
                        {e.action_label}
                        {!e.recorded && (
                          <span title="Reconstructed from the booking record; no audit entry exists for it." style={{ marginLeft: 5, fontWeight: 600, color: C.textMuted }}>
                            {' (from the booking record)'}
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 10.5, color: C.textDim, lineHeight: 1.5 }}>{e.summary}</div>
                      {/* The reason is shown only when it says something the summary does not:
                          decline and withdrawal summaries ARE the reason, so printing it twice
                          is noise. */}
                      {e.reason && e.reason !== e.summary && (
                        <div style={{ fontSize: 10, color: C.textMuted, lineHeight: 1.5, marginTop: 1 }}>{e.reason}</div>
                      )}
                      {expandable && (
                        <button
                          onClick={() => setOpenEntryId(open ? null : e.id)}
                          style={{ background: 'none', border: 'none', padding: '2px 0 0', cursor: 'pointer', fontFamily: F, fontSize: 9.5, color: C.accent, fontWeight: 600 }}
                        >
                          {open ? 'Hide details' : `Show details (${e.changes.length})`}
                        </button>
                      )}
                      {open && (
                        <div style={{ marginTop: 3 }}>
                          {e.changes.map((c) => (
                            <div key={c.field} style={{ fontSize: 10, color: C.textDim, lineHeight: 1.55 }}>
                              <span style={{ color: C.slate, fontWeight: 600 }}>{c.label}:</span> {c.from} to {c.to}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}

                {history && history.entries.length === 0 && history.notes.length === 0 && (
                  <div style={{ fontSize: 10.5, color: C.textMuted }}>No history has been recorded for this booking.</div>
                )}
              </div>
            )}
          </div>
        )}

        {mode === 'return' && (() => {
          const ecd = booking.arr_time_planned;
          const isLate = !!returnAt && !!ecd && new Date(returnAt).getTime() > new Date(ecd).getTime();
          // When late, the user must answer the "was a reason given?" prompt before confirming.
          const ready = !!returnAt && !(isLate && lateReasonGiven === 'unset');
          const body: Record<string, unknown> = returnAt ? { returned_at: new Date(returnAt).toISOString() } : {};
          if (isLate && lateReasonGiven === 'yes' && lateReasonText.trim()) body.late_return_reason = lateReasonText.trim();
          return (
          <div style={{ marginTop: 14 }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: C.textDim, letterSpacing: '0.06em', marginBottom: 3, textTransform: 'uppercase' }}>
              When was the aircraft returned to service?
            </div>
            <input
              type="datetime-local"
              value={returnAt}
              max={nowLocalInput()}
              onChange={(e) => setReturnAt(e.target.value)}
              style={inputStyle}
            />
            <div style={{ fontSize: 10, color: C.textMuted, marginTop: 3, lineHeight: 1.4 }}>
              Defaults to now; backdating allowed (within the maintenance window). Planned ECD is kept for forecasting.
            </div>
            {isLate && (
              <div style={{ marginTop: 10, background: C.amberLight, border: `1px solid ${C.amberBorder}`, borderRadius: 6, padding: '8px 10px' }}>
                <div style={{ fontSize: 11, color: C.amber, fontWeight: 700, marginBottom: 6 }}>
                  Returned after the planned completion ({ecd ? ecd.slice(0, 10) : ''}). Was a reason given for the delay?
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {btn('Yes', lateReasonGiven === 'yes' ? '#fff' : C.textDim, lateReasonGiven === 'yes' ? C.amber : C.surface, C.amberBorder, () => setLateReasonGiven('yes'))}
                  {btn('No', lateReasonGiven === 'no' ? '#fff' : C.textDim, lateReasonGiven === 'no' ? C.amber : C.surface, C.amberBorder, () => { setLateReasonGiven('no'); setLateReasonText(''); })}
                </div>
                {lateReasonGiven === 'yes' && (
                  <textarea value={lateReasonText} onChange={(e) => setLateReasonText(e.target.value)} placeholder="Reason for the delay…" style={{ ...inputStyle, minHeight: 44, resize: 'vertical', marginTop: 6 }} />
                )}
              </div>
            )}
            <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
              {btn('Back', C.textMuted, 'none', C.border, () => setMode('view'))}
              {btn('Confirm returned', '#fff', ready ? C.green : C.pill, ready ? C.green : C.pill,
                () => { if (ready) act('maintenance-return', body); })}
            </div>
          </div>
          );
        })()}

        {/* APPROVE (kickoff §4b) — mandatory written EVIDENCE, plus the phone-call confirmation
            templates. Ben's locked rule is "a note OR an attachment, at least one, neither
            individually mandatory, with the UI stating that written evidence is always preferred".
            Attachments do not exist yet, so stage 1 is: the note is required. */}
        {mode === 'approve' && (() => {
          const ready = !!approveNote.trim();
          const tpl = (which: 'staff' | 'asset', heading: string, t: ConfirmationTemplate) => (
            <div key={which} style={{ marginTop: 8, background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 6, padding: '8px 10px' }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: C.textDim, marginBottom: 4 }}>
                {heading}{t.contact_name ? ` — ${t.contact_name}` : ''}
              </div>
              <div style={{ fontSize: 11, color: C.text, lineHeight: 1.5, whiteSpace: 'pre-wrap', userSelect: 'text' }}>{t.message}</div>
              <div style={{ marginTop: 6 }}>
                {btn(copied === which ? '✓ Copied' : 'Copy message', copied === which ? C.green : C.accent,
                  copied === which ? C.greenLight : C.accentLight, copied === which ? C.green : C.accentBorder,
                  () => { void copyTemplate(which, t.message); })}
              </div>
            </div>
          );
          return (
            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: C.textDim, letterSpacing: '0.06em', marginBottom: 3, textTransform: 'uppercase' }}>
                How was this approved? <span style={{ color: C.red }}>*</span>
              </div>
              {/* The placeholder is the ONLY instruction most people will ever read on a mandatory
                  field, so it has to MODEL the answer rather than gesture at it. Ben's own live
                  entry was "MACKB Available", which records neither WHO was contacted nor WHEN, and
                  an entry like that is worthless months later when someone asks who gave
                  permission. This example names the person, their relationship to the thing being
                  approved, the date and time, and the outcome, in that order. */}
              <textarea
                value={approveNote}
                onChange={(e) => setApproveNote(e.target.value)}
                placeholder="e.g. Rang Peter Nguyen, owner of VH-DYM, 27/08 at 1610. Happy for it to be used."
                style={{ ...inputStyle, minHeight: 54, resize: 'vertical' }}
              />
              {/* Ben's rewrite, 2026-08-27. This ONE line replaces three blocks: the old
                  "Record who gave permission and how", the "Confirmed by phone? Send this" heading,
                  and the "Copy it into Messages…" explanation. The templates below ARE the "eg" in
                  his sentence, so explaining them a second time was just noise on a panel that was
                  already too tall to scroll. */}
              <div style={{ fontSize: 10, color: C.textMuted, marginTop: 3, lineHeight: 1.4 }}>
                Written evidence is always preferred. If confirmed by phone, send a followup text.
              </div>

              {templates && (templates.staff || templates.asset) && (
                <div style={{ marginTop: 10 }}>
                  {templates.staff && tpl('staff', 'Staff availability', templates.staff)}
                  {templates.asset && tpl('asset', 'Aircraft or asset permission', templates.asset)}
                </div>
              )}

              <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                {btn('Back', C.textMuted, 'none', C.border, () => setMode('view'))}
                {btn('Confirm Approve', '#fff', ready ? C.green : C.pill, ready ? C.green : C.pill,
                  () => { if (ready) act('approve', { approval_note: approveNote.trim() }); }, 2)}
              </div>
            </div>
          );
        })()}

        {/* DECLINE / WITHDRAW APPROVAL (kickoff §3.2). Two mandatory inputs: what happens to the
            booking, and why. The disposition has NO default — silently picking one is how bookings
            get lost. Same dialog for a plain decline and for a revocation; only the wording differs. */}
        {mode === 'decline' && (() => {
          const ready = !!reason.trim() && !!disposition && (disposition !== 'cancel' || !!cancelReasonId);
          const chip = (value: 'waitlist' | 'cancel', label: string, help: string) => (
            <button
              key={value}
              onClick={() => setDisposition(value)}
              disabled={busy}
              style={{
                textAlign: 'left', width: '100%', cursor: busy ? 'default' : 'pointer', fontFamily: F,
                background: disposition === value ? C.accentLight : C.surface,
                border: `1px solid ${disposition === value ? C.accent : C.border}`,
                borderRadius: 6, padding: '8px 10px', marginBottom: 6,
              }}
            >
              <div style={{ fontSize: 11, fontWeight: 700, color: disposition === value ? C.accent : C.text }}>{label}</div>
              <div style={{ fontSize: 10, color: C.textMuted, lineHeight: 1.4, marginTop: 2 }}>{help}</div>
            </button>
          );
          return (
            <div style={{ marginTop: 14 }}>
              {approved && (
                <div style={{ background: C.amberLight, border: `1px solid ${C.amberBorder}`, borderRadius: 6, padding: '8px 10px', marginBottom: 10, fontSize: 11, color: C.amber, lineHeight: 1.5 }}>
                  ⚠ This booking is already approved. Withdrawing that approval is a material change to a
                  confirmed booking. The booker and the instructor are notified straight away; the student
                  or client is NOT, so please contact them.
                </div>
              )}
              <div style={{ fontSize: 10, fontWeight: 600, color: C.textDim, letterSpacing: '0.06em', marginBottom: 4, textTransform: 'uppercase' }}>
                What happens to the booking? <span style={{ color: C.red }}>*</span>
              </div>
              {chip('waitlist', 'Waitlist it', 'Keeps the booking and frees the aircraft, instructor and student. It stays on the waitlist until you allocate another aircraft or time.')}
              {chip('cancel', 'Cancel the booking', 'The booking will not go ahead. You will be asked for a cancellation reason.')}

              {disposition === 'cancel' && (
                <>
                  <div style={{ fontSize: 10, fontWeight: 600, color: C.textDim, letterSpacing: '0.06em', margin: '4px 0 3px', textTransform: 'uppercase' }}>
                    Cancellation reason <span style={{ color: C.red }}>*</span>
                  </div>
                  <select value={cancelReasonId} onChange={(e) => setCancelReasonId(e.target.value)} style={inputStyle}>
                    <option value="">— select a reason —</option>
                    {cancelReasons.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
                  </select>
                  {cancelReasons.length === 0 && (
                    <div style={{ fontSize: 10, color: C.textMuted, marginTop: 3, lineHeight: 1.4 }}>
                      No cancellation reasons available (none configured, or you’re offline). An admin can add them under Admin → Reason codes.
                    </div>
                  )}
                </>
              )}

              <div style={{ fontSize: 10, fontWeight: 600, color: C.textDim, letterSpacing: '0.06em', margin: '8px 0 3px', textTransform: 'uppercase' }}>
                {approved ? 'Why is the approval being withdrawn?' : 'Decline reason'} <span style={{ color: C.red }}>*</span>
              </div>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={approved ? 'Why is this approval being withdrawn?' : 'Why is this booking declined?'}
                style={{ ...inputStyle, minHeight: 54, resize: 'vertical' }}
              />
              <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                {btn('Back', C.textMuted, 'none', C.border, () => setMode('view'))}
                {btn(approved ? 'Confirm Withdrawal' : 'Confirm Decline', '#fff', ready ? C.red : C.pill, ready ? C.red : C.pill,
                  () => {
                    if (!ready) return;
                    act('decline', disposition === 'cancel'
                      ? { disposition, approval_note: reason.trim(), cancel_reason_id: cancelReasonId }
                      : { disposition, approval_note: reason.trim() });
                  }, 2)}
              </div>
            </div>
          );
        })()}

        {/* CANCEL — Feature C: a mandatory CODED cancellation reason + an optional note. The submit
            body sends cancel_reason = the note OR the selected label, so it is ALWAYS non-empty and
            the offline replay path (shared cancelSchema requires a free-text cancel_reason) stays valid. */}
        {mode === 'cancel' && (() => {
          const selectedLabel = cancelReasons.find((r) => r.id === cancelReasonId)?.label ?? '';
          const ready = !!cancelReasonId;
          return (
            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: C.textDim, letterSpacing: '0.06em', marginBottom: 3, textTransform: 'uppercase' }}>
                Cancellation reason <span style={{ color: C.red }}>*</span>
              </div>
              <select value={cancelReasonId} onChange={(e) => setCancelReasonId(e.target.value)} style={inputStyle}>
                <option value="">— select a reason —</option>
                {cancelReasons.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
              </select>
              {cancelReasons.length === 0 && (
                <div style={{ fontSize: 10, color: C.textMuted, marginTop: 3, lineHeight: 1.4 }}>
                  No cancellation reasons available (none configured, or you’re offline). An admin can add them under Admin → Reason codes.
                </div>
              )}
              <div style={{ fontSize: 10, fontWeight: 600, color: C.textDim, letterSpacing: '0.06em', margin: '8px 0 3px', textTransform: 'uppercase' }}>Note (optional)</div>
              <textarea
                value={cancelNote}
                onChange={(e) => setCancelNote(e.target.value)}
                placeholder="Optional extra detail…"
                style={{ ...inputStyle, minHeight: 44, resize: 'vertical' }}
              />
              <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                {btn('Back', C.textMuted, 'none', C.border, () => setMode('view'))}
                {btn('Confirm Cancel', '#fff', ready ? C.red : C.pill, ready ? C.red : C.pill,
                  () => { if (!ready) return; act('cancel', { cancel_reason_id: cancelReasonId, cancel_reason: cancelNote.trim() || selectedLabel }); }, 2)}
              </div>
            </div>
          );
        })()}
      </div>
    </div>
    </>
  );
}
